import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  HomeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { getPasswordResetSummary, confirmPasswordReset } from '../services/passwordResetService';

// ── Strength meter ────────────────────────────────────────────────────────────
const scorePassword = (pw) => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  return score; // 0-5
};

const STRENGTH_META = [
  { label: '',        color: 'bg-gray-200' },
  { label: 'Weak',    color: 'bg-red-400'  },
  { label: 'Fair',    color: 'bg-orange-400' },
  { label: 'Good',    color: 'bg-yellow-400' },
  { label: 'Strong',  color: 'bg-emerald-500' },
  { label: 'Excellent', color: 'bg-emerald-600' },
];

const StrengthBar = ({ password }) => {
  const score = scorePassword(password);
  const meta  = STRENGTH_META[score];
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1,2,3,4,5].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= score ? meta.color : 'bg-gray-200'}`}
          />
        ))}
      </div>
      {password && (
        <p className={`text-xs font-medium ${score <= 1 ? 'text-red-500' : score <= 2 ? 'text-orange-500' : score <= 3 ? 'text-yellow-600' : 'text-emerald-600'}`}>
          {meta.label}
        </p>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  // Token validation state
  const [tokenState, setTokenState] = useState('loading'); // 'loading'|'valid'|'invalid'
  const [maskedEmail, setMaskedEmail] = useState('');
  const [tokenError, setTokenError] = useState('');

  // Form state
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [showPw, setShowPw]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [formError, setFormError]   = useState('');
  const [success, setSuccess]       = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenState('invalid');
      setTokenError('No reset token found in the URL.');
      return;
    }
    getPasswordResetSummary(token)
      .then((data) => {
        setMaskedEmail(data.masked_email);
        setTokenState('valid');
      })
      .catch((err) => {
        setTokenState('invalid');
        setTokenError(err.message || 'This reset link is invalid or has expired.');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(token, password);
      setSuccess(true);
      // Redirect to login after 3 s with a flash message.
      setTimeout(() => navigate('/login', { state: { flash: 'Password reset successfully. Please sign in.' } }), 3000);
    } catch (err) {
      setFormError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 px-4 py-12">
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-blue-500/20 backdrop-blur-3xl pointer-events-none" />

      <div className="max-w-md w-full relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-3 rounded-2xl shadow-lg">
              <HomeIcon className="h-12 w-12 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">Set new password</h2>
          {maskedEmail && (
            <p className="mt-2 text-sm text-gray-600">
              Resetting password for <span className="font-medium text-gray-800">{maskedEmail}</span>
            </p>
          )}
        </div>

        <div className="bg-white/80 backdrop-blur-md shadow-2xl rounded-2xl border border-white/20 p-8">

          {/* Loading skeleton */}
          {tokenState === 'loading' && (
            <div className="flex flex-col items-center py-8 gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              <p className="text-sm text-gray-500">Validating link…</p>
            </div>
          )}

          {/* Invalid / expired token */}
          {tokenState === 'invalid' && (
            <div className="text-center">
              <ExclamationTriangleIcon className="mx-auto h-14 w-14 text-red-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Link invalid or expired</h3>
              <p className="text-sm text-gray-600 mb-6">{tokenError}</p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center justify-center w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold shadow-lg hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 mb-3"
              >
                Request a new link
              </Link>
              <Link to="/login" className="inline-flex items-center text-sm text-gray-500 hover:text-emerald-600 transition-colors">
                <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to sign in
              </Link>
            </div>
          )}

          {/* Success */}
          {tokenState === 'valid' && success && (
            <div className="text-center">
              <CheckCircleIcon className="mx-auto h-16 w-16 text-emerald-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Password updated!</h3>
              <p className="text-sm text-gray-600 mb-6">
                Your password has been reset. Redirecting you to sign in…
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold shadow-lg hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200"
              >
                Sign in now
              </Link>
            </div>
          )}

          {/* Form */}
          {tokenState === 'valid' && !success && (
            <form onSubmit={handleSubmit} noValidate>
              {formError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{formError}</p>
                </div>
              )}

              {/* New password */}
              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  New password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPw ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 8 chars, letters + digits"
                    className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 text-gray-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                <StrengthBar password={password} />
              </div>

              {/* Confirm password */}
              <div className="mb-6">
                <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm new password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirm"
                    type={showConfirm ? 'text' : 'password'}
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat your new password"
                    className={`block w-full pl-10 pr-10 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 text-gray-900 ${
                      confirm && confirm !== password
                        ? 'border-red-400 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-emerald-500'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                {confirm && confirm !== password && (
                  <p className="mt-1 text-xs text-red-500">Passwords don't match.</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !password || !confirm || password !== confirm}
                className="w-full flex justify-center items-center py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold shadow-lg hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Updating…
                  </>
                ) : (
                  'Update password'
                )}
              </button>

              <div className="mt-5 text-center">
                <Link to="/login" className="inline-flex items-center text-sm text-gray-500 hover:text-emerald-600 transition-colors">
                  <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

